"use client";

import { create } from "zustand";

import type { ContactVerificationVariant } from "@/features/account-profile/types/contact-verification.types";
import type { ProfileCar, ProfileValues } from "@/features/account-profile/types/account-profile-ui.types";

export type ProfileVerifyIntent = {
    variant: ContactVerificationVariant;
    initialValue: string;
    returnEditing: boolean;
    /** Snapshot used to diff POST /v1/me after OTP verify (full-page flow). */
    profileSnapshot: ProfileValues;
    carsSnapshot: ProfileCar[];
};

export type ProfileVerifyComplete = {
    variant: ContactVerificationVariant;
    value: string;
    returnEditing: boolean;
    message: string;
    /** When true, profile was persisted on the verify page; consume skips redundant PATCH-style local merge. */
    profileSaved?: boolean;
    /** Draft/cars captured before navigating to the OTP flow, restored on return so unverified fields don't blank out. */
    profileSnapshot?: ProfileValues;
    carsSnapshot?: ProfileCar[];
};

interface ProfileVerifyHandoffState {
    intent: ProfileVerifyIntent | null;
    successViewPayload: ProfileVerifyComplete | null;
    pendingContactApply: ProfileVerifyComplete | null;
    setIntent: (payload: ProfileVerifyIntent) => void;
    clearIntent: () => void;
    finishVerification: (payload: ProfileVerifyComplete) => void;
    consumePendingContactApply: () => ProfileVerifyComplete | null;
    clearSuccessViewPayload: () => void;
}

export const useProfileVerifyHandoffStore = create<ProfileVerifyHandoffState>((set, get) => ({
    intent: null,
    successViewPayload: null,
    pendingContactApply: null,
    setIntent: (payload) =>
        set({
            intent: payload,
            successViewPayload: null,
            pendingContactApply: null,
        }),
    clearIntent: () => set({ intent: null }),
    finishVerification: (payload) =>
        set({
            successViewPayload: payload,
            pendingContactApply: payload,
            intent: null,
        }),
    consumePendingContactApply: () => {
        const p = get().pendingContactApply;
        if (p) set({ pendingContactApply: null });
        return p;
    },
    clearSuccessViewPayload: () => set({ successViewPayload: null }),
}));
